import {
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  Link,
  TypographyProps,
  SxProps,
} from '@mui/material';
import Pro from '@jobseeker/lotus/elements/Pro';
import Tooltip from '@jobseeker/lotus/elements/Tooltip';
import {Switch} from '@jobseeker/lotus/elements';
import {getProLink} from '@jobseeker/lotus/utils';

const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ListPaper = styled(Paper)(({theme}) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fff',
  gap: theme.spacing(1.5),
}));

type NotificationSettingRowProps = {
  heading: string;
  description: string | React.ReactNode;
  isPro?: boolean;
  proFeature?: boolean;
  emailValue: boolean;
  pushValue: boolean;
  onEmailChange: () => void;
  onPushChange: () => void;
  noPush?: boolean;
  isLastItem?: boolean; // when true, divider is hidden
  mainSx?: SxProps;
};

const NotificationSettingRow = ({
  heading,
  description,
  isPro = true,
  proFeature,
  emailValue,
  pushValue,
  onEmailChange,
  onPushChange,
  noPush = false,
  isLastItem = false,
  mainSx,
}: NotificationSettingRowProps) => {
  const theme = useTheme();
  const isProLocked = proFeature && !isPro;

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';


  return (
    <Box
      data-testid="notification_list_item"
      className="notification_list_item"
      sx={{position: 'relative', transition: 'all ease 0.2s', ...mainSx}}
    >
      <ListPaper
        elevation={0}
        sx={{py: {xxs: 1.5, md: 2}, px: {xxs: 1.5, md: 2}}}
      >
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Box display="flex" flexDirection="column" flex={1}>
            <Typography
              fontWeight={isAppIimjobs ? 500 : 600}
              lineHeight="20px"
              component="span"
              fontSize={{xxs: 13, md: 14}}
              color={theme.palette.secondary.main}
              sx={{
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {heading}
              {isProLocked && (
                <Tooltip
                  placement="bottom-start"
                  title={
                    <Box sx={{pr: {md: 12.375}}}>
                      <Typography
                        component="p"
                        mb={{xxs: 0.5, md: 0}}
                        variant="subtitle3"
                      >
                        To avail this PRO-Feature
                      </Typography>
                      <Link
                        href={getProLink('ja', '123', '20')}
                        target="_self"
                        sx={{
                          fontSize: '12px',
                          fontWeight: '500',
                          textDecoration: 'none',
                          '&:hover': {textDecoration: 'none'},
                          '&:focus, &:active': {textDecoration: 'none'},
                        }}
                      >
                        Upgrade to PRO
                      </Link>
                    </Box>
                  }
                  arrow
                >
                  <Box display="block" ml={theme.spacing(0.5)}>
                    <Pro bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)" />
                  </Box>
                </Tooltip>
              )}
            </Typography>
            <Typography
              fontWeight={400}
              lineHeight={{xxs: '16px', md: '20px'}}
              fontSize={{xxs: 11, md: 12}}
              sx={{
                color: 'var(--Grey-font-Color, #7E7E7E)',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'block',
              }}
            >
              {description}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={{xxs: 3, sm: 10}}
            flexShrink={0}
          >
            <Box width={40} display="flex" justifyContent="center">
              {!isProLocked && (
                <Switch checked={emailValue} onChange={onEmailChange} />
              )}
            </Box>
            <Box width={40} display="flex" justifyContent="center">
              {!noPush && (
                <Switch checked={pushValue} onChange={onPushChange} />
              )}
            </Box>
          </Box>
        </Box>
      </ListPaper>
      {!isLastItem && (
        <Box
          sx={{
            mx: {xxs: 0, sm: 2},
            borderBottom: 'solid 1px',
            borderColor: 'border.lightGray',
          }}
        />
      )}
    </Box>
  );
};

export default NotificationSettingRow;
